import { useMemo } from 'react';
import {
  parseEntityRef,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { getEntityRelations } from '@backstage/plugin-catalog-react';
import { SupportButton } from '@internal/plugin-catalog';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useLocation } from 'react-router-dom';
import useAsync from 'react-use/esm/useAsync';

const parseTemplatePath = (pathName?: string) => {
  if (!pathName) {
    return undefined;
  }

  const matchedPath = pathName.match(
    /^\/create\/templates\/([^/]+)\/([^/?#]+)/,
  );

  if (!matchedPath) {
    return undefined;
  }

  return {
    namespace: matchedPath[1],
    templateName: matchedPath[2],
  };
};

export const TemplateSupportButton = () => {
  const { pathname } = useLocation();
  const catalogApi = useApi(catalogApiRef);

  const parsedTemplatePath = useMemo(
    () => parseTemplatePath(pathname),
    [pathname],
  );

  const entityRef = useMemo(() => {
    if (!parsedTemplatePath?.templateName) {
      return undefined;
    }

    return stringifyEntityRef({
      kind: 'Template',
      namespace: parsedTemplatePath.namespace ?? 'default',
      name: parsedTemplatePath.templateName,
    });
  }, [parsedTemplatePath]);

  const { value: supportEntity } = useAsync(async () => {
    if (!entityRef) {
      return undefined;
    }

    const templateEntity = await catalogApi.getEntityByRef(entityRef);

    if (!templateEntity) {
      return undefined;
    }

    const ownedByRelations = getEntityRelations(
      templateEntity,
      RELATION_OWNED_BY,
    );
    if (ownedByRelations.length > 0) {
      return templateEntity;
    }

    const ownerRef = (templateEntity as any).spec?.owner;
    if (!ownerRef) {
      return templateEntity;
    }

    const parsedOwnerRef = parseEntityRef(ownerRef, {
      defaultKind: 'Group',
      defaultNamespace: 'default',
    });

    const ownerEntityRef = stringifyEntityRef({
      kind: parsedOwnerRef.kind,
      namespace: parsedOwnerRef.namespace,
      name: parsedOwnerRef.name,
    });

    const ownerEntity = await catalogApi.getEntityByRef(ownerEntityRef);
    return ownerEntity ?? templateEntity;
  }, [catalogApi, entityRef]);

  return supportEntity ? <SupportButton entity={supportEntity} /> : null;
};
